import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Radio, PlayCircle, Trash2, CalendarPlus, Upload, Star } from "lucide-react";
import { toast } from "sonner";
import MuxPlayer from "@mux/mux-player-react";
import MuxUploader from "@mux/mux-uploader-react";
import { LiveBroadcaster } from "@/components/LiveBroadcaster";
import { MobileBroadcastPanel } from "@/components/MobileBroadcastPanel";
import { UpcomingBroadcasts } from "@/components/UpcomingBroadcasts";
import { SECTIONS } from "@/lib/sections";

const fmtDuration = (s) => {
  if (!s) return "";
  const m = Math.floor(s / 60);
  return `${m} min`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const Live = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState({ configured: false, active: null });
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pre-broadcast modal
  const [showStartModal, setShowStartModal] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftSection, setDraftSection] = useState("art-de-vivre");
  const [starting, setStarting] = useState(false);

  // Active broadcast (editor side)
  const [activeBroadcast, setActiveBroadcast] = useState(null);

  // Post-broadcast modal
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [finishOpts, setFinishOpts] = useState({
    save_to_archive: true,
    show_on_profile: true,
    post_to_room: false,
    title: "",
    section: "",
  });
  const [finishing, setFinishing] = useState(false);

  // Schedule a future broadcast
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedDraft, setSchedDraft] = useState({
    title: "",
    section: "art-de-vivre",
    scheduled_at: "",
    description: "",
    send_save_the_date_push: false,
  });
  const [scheduling, setScheduling] = useState(false);

  const submitSchedule = async () => {
    if (!schedDraft.title.trim() || !schedDraft.scheduled_at) {
      toast.error("Please add a title and a date.");
      return;
    }
    setScheduling(true);
    try {
      // Convert local datetime-local string into ISO with timezone.
      const isoUtc = new Date(schedDraft.scheduled_at).toISOString();
      await api.post("/live/schedule", { ...schedDraft, scheduled_at: isoUtc });
      toast.success("Broadcast scheduled.");
      setShowScheduleModal(false);
      setSchedDraft({ title: "", section: "art-de-vivre", scheduled_at: "", description: "", send_save_the_date_push: false });
      // Bump key so UpcomingBroadcasts refreshes
      setSchedRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not schedule.");
    } finally {
      setScheduling(false);
    }
  };
  const [schedRefreshKey, setSchedRefreshKey] = useState(0);

  // Upload video (pre-recorded broadcast via Mux Direct Upload)
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadDraft, setUploadDraft] = useState({
    title: "",
    section: "art-de-vivre",
    description: "",
    notify_subscribers: true,
  });
  const [uploadEndpoint, setUploadEndpoint] = useState(null); // { broadcast_id, upload_url }
  const [uploadStage, setUploadStage] = useState("form"); // 'form' | 'uploading' | 'complete'
  const [preparingUpload, setPreparingUpload] = useState(false);

  // Manual re-sync for stuck encoding cards
  const [syncingId, setSyncingId] = useState(null);
  const syncBroadcast = async (id) => {
    setSyncingId(id);
    try {
      const { data } = await api.post(`/live/broadcasts/${id}/sync`);
      if (data.state === "ended") {
        toast.success("Ready — it's in your archive.");
      } else if (data.state === "failed") {
        toast.error("Mux couldn't process this upload. Please try again.");
      } else {
        toast("Still encoding. This can take a few minutes for long videos.");
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not check status.");
    } finally {
      setSyncingId(null);
    }
  };

  // Toggle "Feature on Landing" ⭐ — only one broadcast can be featured at a time.
  const [featuringId, setFeaturingId] = useState(null);
  const toggleFeature = async (e, id) => {
    e.stopPropagation();
    setFeaturingId(id);
    try {
      const { data } = await api.post(`/live/broadcasts/${id}/feature`);
      toast.success(
        data.featured_on_landing
          ? "Featured on Landing — visitors will see it first."
          : "Unfeatured. Landing will fall back to your newest broadcast."
      );
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update featured broadcast.");
    } finally {
      setFeaturingId(null);
    }
  };

  const beginUpload = async () => {
    if (!uploadDraft.title.trim()) {
      toast.error("Give your video a title.");
      return;
    }
    setPreparingUpload(true);
    try {
      // Always use "*" for CORS — iPhone Safari + PWAs can report subtly
      // different origins (with/without :port, home-screen vs Safari) which
      // makes an origin-specific cors_origin cause "Server responded with 0"
      // errors on mobile uploads. Wildcard works everywhere and is safe here
      // because the upload URL is a one-shot signed URL from Mux.
      const { data } = await api.post("/live/upload", {
        title: uploadDraft.title.trim(),
        section: uploadDraft.section,
        description: uploadDraft.description.trim(),
        cors_origin: "*",
        notify_subscribers: !!uploadDraft.notify_subscribers,
      });
      setUploadEndpoint(data);
      setUploadStage("uploading");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not prepare upload.");
    } finally {
      setPreparingUpload(false);
    }
  };

  const closeUploadModal = () => {
    if (uploadStage === "uploading") {
      if (!window.confirm("Cancel this upload?")) return;
    }
    setShowUploadModal(false);
    setUploadDraft({ title: "", section: "art-de-vivre", description: "", notify_subscribers: true });
    setUploadEndpoint(null);
    setUploadStage("form");
  };

  const handleUploadSuccess = async () => {
    if (!uploadEndpoint) return;
    try {
      await api.post(`/live/upload/${uploadEndpoint.broadcast_id}/complete`);
    } catch (err) {
      // Non-fatal — webhook will still move it to ready
    }
    setUploadStage("complete");
    toast.success("Uploaded. Encoding now — appears in the archive within a minute.");
    await load();
  };

  const startScheduledBroadcast = (schedule) => {
    // Editor clicked "Start now" on a scheduled item — pre-fill the Go Live
    // modal with the schedule's title + room and open it.
    setDraftTitle(schedule?.title || "");
    setDraftSection(schedule?.section || "art-de-vivre");
    setShowStartModal(true);
  };

  const load = async () => {
    try {
      const [s, b] = await Promise.all([
        api.get("/live/status"),
        api.get("/live/broadcasts", { params: { limit: 24 } }),
      ]);
      setStatus(s.data);
      setBroadcasts(b.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // refresh every 15s
    return () => clearInterval(t);
  }, []);

  const startBroadcast = async () => {
    if (!draftTitle.trim()) return toast.error("Give your broadcast a title.");
    if (!draftSection) return toast.error("Choose a room.");
    setStarting(true);
    try {
      const { data } = await api.post("/live/start", {
        title: draftTitle.trim(),
        section: draftSection,
      });
      setActiveBroadcast(data);
      setShowStartModal(false);
      toast.success("Broadcast started. Camera connecting…");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start broadcast.");
    } finally {
      setStarting(false);
    }
  };

  const onBroadcasterEnded = () => {
    if (!activeBroadcast) return;
    setFinishOpts({
      save_to_archive: true,
      show_on_profile: true,
      post_to_room: false,
      title: activeBroadcast.title || "",
      section: activeBroadcast.section || "art-de-vivre",
    });
    setShowFinishModal(true);
  };

  const confirmFinish = async (save) => {
    if (!activeBroadcast) return;
    setFinishing(true);
    try {
      if (save) {
        await api.post(`/live/${activeBroadcast.id}/finish`, finishOpts);
        toast.success("Broadcast saved.");
      } else {
        await api.post(`/live/${activeBroadcast.id}/finish`, {
          ...finishOpts,
          save_to_archive: false,
          show_on_profile: false,
        });
        await api.delete(`/live/${activeBroadcast.id}`);
        toast.success("Broadcast discarded.");
      }
      setShowFinishModal(false);
      setActiveBroadcast(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save.");
    } finally {
      setFinishing(false);
    }
  };

  const deleteFromArchive = async (e, broadcastId) => {
    e.stopPropagation();
    if (!window.confirm("Delete this broadcast permanently?")) return;
    try {
      await api.delete(`/live/${broadcastId}`);
      toast.success("Broadcast deleted.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete.");
    }
  };

  const isEditor = !!user?.is_editor;

  return (
    <div data-testid="live-page" className="app-shell">
      <Navbar />
      <div className="container-belle" style={{ paddingTop: 110, paddingBottom: 80 }}>
        {/* Hero */}
        <div className="overline" style={{ color: "var(--brand-accent)" }}>Le salon · live</div>
        <h1 className="editorial-h1" style={{ fontSize: "clamp(40px, 5.5vw, 64px)", marginTop: 12, marginBottom: 16 }}>
          Live broadcasts.
        </h1>
        <p className="editorial-body" style={{ fontSize: 18, color: "var(--text-secondary)", maxWidth: 640, marginBottom: 14 }}>
          Live conversations, special guests, and beautiful moments from Paris and beyond.
        </p>
        <p
          className="editorial-body"
          data-testid="broadcast-notifications-nudge"
          style={{ fontSize: 14, color: "var(--text-muted)", maxWidth: 640, marginBottom: 40, fontStyle: "italic" }}
        >
          Be sure to{" "}
          <Link
            to="/settings"
            data-testid="broadcast-notifications-link"
            className="link-underline"
            style={{ color: "var(--brand-accent)" }}
          >
            turn on notifications
          </Link>{" "}
          to receive a reminder here.
        </p>

        {/* Active broadcaster (editor side) */}
        {activeBroadcast && (
          <div style={{ marginBottom: 48 }}>
            <LiveBroadcaster broadcast={activeBroadcast} onEnded={onBroadcasterEnded} />
          </div>
        )}

        {/* Live now (viewer side) */}
        {!activeBroadcast && status.active && (
          <div style={{ marginBottom: 48, padding: 24, background: "var(--surface)", border: "1px solid var(--border-default)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
              <span
                data-testid="live-now-pill"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 14px",
                  background: "var(--brand-accent)",
                  color: "#FAF9F5",
                  fontFamily: "'Outfit', sans-serif",
                  fontSize: 11,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  fontWeight: 600,
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#FAF9F5", animation: "lvpulse 1.4s ease-in-out infinite" }} />
                Live now
              </span>
              <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22 }}>
                {status.active.title || "An untitled broadcast"}
              </span>
            </div>
            <div style={{ aspectRatio: "16 / 9", background: "#000", overflow: "hidden" }}>
              <MuxPlayer
                playbackId={status.active.live_playback_id}
                streamType="live"
                autoPlay
                style={{ width: "100%", height: "100%" }}
                data-testid="live-now-player"
              />
            </div>
          </div>
        )}

        {/* Editor "Go Live" CTA */}
        {!activeBroadcast && isEditor && (
          <div style={{ marginBottom: 56 }}>
            <div
              style={{
                padding: 28,
                background: "var(--bg-alt)",
                border: "1px solid var(--border-default)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 20,
                flexWrap: "wrap",
              }}
            >
              <div>
                <div className="overline" style={{ color: "var(--brand-accent)" }}>For you, editor</div>
                <h2 className="editorial-h2" style={{ fontSize: 24, marginTop: 8, marginBottom: 6 }}>
                  Open a broadcast.
                </h2>
                <p className="editorial-body" style={{ fontSize: 14, color: "var(--text-muted)", margin: 0 }}>
                  Choose a title and a room — your members will be listening.
                </p>
              </div>
              <button
                type="button"
                data-testid="open-go-live-modal"
                onClick={() => setShowStartModal(true)}
                className="btn-primary"
                style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "var(--brand-accent)" }}
              >
                <Radio size={16} /> Go Live
              </button>
            </div>
            <MobileBroadcastPanel onSaved={load} />
            <div
              style={{
                background: "var(--bg-alt)",
                border: "1px solid var(--border-default)",
                padding: "20px 24px",
                display: "flex",
                gap: 16,
                alignItems: "center",
                flexWrap: "wrap",
                marginTop: 16,
              }}
            >
              <div style={{ flex: 1, minWidth: 220 }}>
                <div className="overline" style={{ color: "var(--brand-accent)" }}>Upload a pre-recorded video</div>
                <p className="editorial-body" style={{ fontSize: 13, color: "var(--text-secondary)", margin: "4px 0 0 0", fontStyle: "italic" }}>
                  For polished editions — no length limit. Encodes in the background.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowUploadModal(true)}
                data-testid="open-upload-modal"
                className="btn-secondary"
                style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                <Upload size={14} /> Upload video
              </button>
            </div>
            <div
              style={{
                background: "var(--bg-alt)",
                border: "1px solid var(--border-default)",
                padding: "20px 24px",
                display: "flex",
                gap: 16,
                alignItems: "center",
                flexWrap: "wrap",
                marginTop: 16,
              }}
            >
              <div style={{ flex: 1, minWidth: 220 }}>
                <div className="overline" style={{ color: "var(--brand-accent)" }}>Schedule a broadcast</div>
                <p className="editorial-body" style={{ fontSize: 13, color: "var(--text-secondary)", margin: "4px 0 0 0", fontStyle: "italic" }}>
                  Tell members in advance so they can save the time.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowScheduleModal(true)}
                data-testid="open-schedule-modal"
                className="btn-secondary"
                style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                <CalendarPlus size={14} /> Schedule
              </button>
            </div>
          </div>
        )}

        {/* Upcoming broadcasts — visible to everyone */}
        <UpcomingBroadcasts
          key={schedRefreshKey}
          variant="live"
          onStartNow={startScheduledBroadcast}
        />

        {/* Archive */}
        <div className="overline" style={{ color: "var(--brand-accent)", marginBottom: 18 }}>
          Past broadcasts
        </div>
        {loading ? (
          <p className="editorial-body" style={{ fontStyle: "italic", color: "var(--text-muted)" }}>Loading…</p>
        ) : broadcasts.length === 0 ? (
          <p className="editorial-body" style={{ fontStyle: "italic", color: "var(--text-muted)", fontSize: 16 }}>
            No broadcasts yet. When the salon opens for the first time, the recording will live here.
          </p>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 24 }}>
            {broadcasts.map((b) => {
              const isEncoding = b.state === "uploading" || b.state === "processing" || (b.state === "ended" && !b.asset_playback_id);
              return (
              <div key={b.id} style={{ position: "relative" }}>
                <button
                  type="button"
                  data-testid={`archive-broadcast-${b.id}`}
                  onClick={() => !isEncoding && navigate(`/broadcast/${b.id}`)}
                  disabled={isEncoding}
                  style={{
                    textAlign: "left",
                    background: "var(--surface)",
                    border: "1px solid var(--border-default)",
                    padding: 0,
                    cursor: isEncoding ? "default" : "pointer",
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    opacity: isEncoding ? 0.85 : 1,
                  }}
                >
                <div style={{ position: "relative", aspectRatio: "16 / 9", background: "#1a1a1a", overflow: "hidden" }}>
                  {isEncoding ? (
                    <div
                      style={{
                        width: "100%", height: "100%", display: "grid", placeItems: "center",
                        color: "#FAF9F5", background: "linear-gradient(135deg, #2a1c1e, #1a1a1a)",
                        gap: 8, textAlign: "center", padding: 12,
                      }}
                      data-testid={`archive-encoding-${b.id}`}
                    >
                      <div style={{
                        width: 26, height: 26, borderRadius: "50%",
                        border: "2px solid rgba(250,249,245,0.25)",
                        borderTopColor: "var(--brand-accent)",
                        animation: "lvspin 0.9s linear infinite",
                      }} />
                      <div className="overline" style={{ fontSize: 10, color: "rgba(250,249,245,0.85)" }}>
                        {b.state === "uploading" ? "Uploading" : b.source === "upload" ? "Encoding" : "Finalizing"}
                      </div>
                    </div>
                  ) : b.custom_thumbnail_url ? (
                    <img
                      loading="lazy" decoding="async" src={b.custom_thumbnail_url}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : b.asset_playback_id ? (
                    <img
                      loading="lazy" decoding="async" src={`https://image.mux.com/${b.asset_playback_id}/thumbnail.jpg?width=560&time=${b.thumbnail_time || 2}`}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: "#FAF9F5", opacity: 0.5 }}>
                      <PlayCircle size={40} strokeWidth={1.2} />
                    </div>
                  )}
                </div>
                <div style={{ padding: 16 }}>
                  <div className="overline" style={{ fontSize: 10, color: "var(--brand-accent)" }}>
                    {b.section || "broadcast"} {b.asset_duration_seconds ? `· ${fmtDuration(b.asset_duration_seconds)}` : ""}
                  </div>
                  <div className="font-serif" style={{ fontSize: 20, marginTop: 6, lineHeight: 1.25, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span>{b.title || "Untitled broadcast"}</span>
                    {b.featured_on_landing && (
                      <span
                        data-testid={`featured-chip-${b.id}`}
                        title="Currently featured on your Landing page"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          fontSize: 9,
                          letterSpacing: "0.18em",
                          textTransform: "uppercase",
                          fontFamily: "'Inter', sans-serif",
                          padding: "3px 8px",
                          border: "1px solid var(--brand-accent)",
                          color: "var(--brand-accent)",
                          borderRadius: 20,
                          background: "rgba(120, 34, 45, 0.06)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Star size={9} strokeWidth={2} fill="currentColor" /> On Landing
                      </span>
                    )}
                  </div>
                  <div className="overline" style={{ fontSize: 10, marginTop: 6, color: "var(--text-muted)" }}>
                    {isEncoding ? "Encoding — appears when ready" : fmtDate(b.ended_at)}
                  </div>
                </div>
              </button>
              {isEncoding && user?.id === b.editor_id && (
                <button
                  type="button"
                  data-testid={`archive-sync-${b.id}`}
                  onClick={(e) => { e.stopPropagation(); syncBroadcast(b.id); }}
                  disabled={syncingId === b.id}
                  aria-label="Check status"
                  title="Check encoding status"
                  style={{
                    position: "absolute",
                    top: 10,
                    left: 10,
                    padding: "6px 10px",
                    fontSize: 10,
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    borderRadius: 20,
                    border: "1px solid rgba(250,249,245,0.35)",
                    background: "rgba(0,0,0,0.55)",
                    color: "#FAF9F5",
                    cursor: "pointer",
                  }}
                >
                  {syncingId === b.id ? "Checking…" : "Check status"}
                </button>
              )}
              {isEditor && user?.id === b.editor_id && !isEncoding && (
                <>
                  <button
                    type="button"
                    data-testid={`archive-feature-${b.id}`}
                    onClick={(e) => toggleFeature(e, b.id)}
                    disabled={featuringId === b.id}
                    aria-label={b.featured_on_landing ? "Unfeature from Landing" : "Feature on Landing"}
                    title={b.featured_on_landing ? "Featured on Landing — click to remove" : "Feature on Landing page"}
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 50,
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      border: "none",
                      background: b.featured_on_landing ? "var(--brand-accent)" : "rgba(0,0,0,0.55)",
                      color: "#FAF9F5",
                      display: "grid",
                      placeItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <Star size={14} strokeWidth={1.6} fill={b.featured_on_landing ? "#FAF9F5" : "none"} />
                  </button>
                  <button
                    type="button"
                    data-testid={`archive-delete-${b.id}`}
                    onClick={(e) => deleteFromArchive(e, b.id)}
                    aria-label="Delete broadcast"
                    title="Delete broadcast"
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      border: "none",
                      background: "rgba(0,0,0,0.55)",
                      color: "#FAF9F5",
                      display: "grid",
                      placeItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <Trash2 size={14} strokeWidth={1.6} />
                  </button>
                </>
              )}
            </div>
              );
            })}
          </div>
      )}
      </div>
      <Footer />

      {/* Pre-broadcast modal */}
      {showStartModal && (
        <ModalShell onClose={() => setShowStartModal(false)}>
          <div className="overline" style={{ color: "var(--brand-accent)" }}>Go Live</div>
          <h2 className="editorial-h2" style={{ fontSize: 28, marginTop: 6, marginBottom: 24 }}>
            Set the scene.
          </h2>
          <label className="overline">Title</label>
          <input
            className="input-belle"
            data-testid="start-title"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            placeholder="A Sunday morning in the kitchen"
            autoFocus
          />
          <div style={{ marginTop: 22 }}>
            <label className="overline">Room</label>
            <select
              className="input-belle"
              data-testid="start-section"
              value={draftSection}
              onChange={(e) => setDraftSection(e.target.value)}
              style={{ width: "100%" }}
            >
              {SECTIONS.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 28, justifyContent: "flex-end" }}>
            <button type="button" className="btn-secondary" onClick={() => setShowStartModal(false)}>
              Cancel
            </button>
            <button
              type="button"
              data-testid="confirm-start-broadcast"
              className="btn-primary"
              disabled={starting}
              onClick={startBroadcast}
              style={{ background: "var(--brand-accent)" }}
            >
              {starting ? "Opening…" : "Start broadcast"}
            </button>
          </div>
        </ModalShell>
      )}

      {/* Post-broadcast modal */}
      {showFinishModal && (
        <ModalShell onClose={() => {}}>
          <div className="overline" style={{ color: "var(--brand-accent)" }}>Broadcast ended</div>
          <h2 className="editorial-h2" style={{ fontSize: 28, marginTop: 6, marginBottom: 18 }}>
            Save the recording?
          </h2>
          <p className="editorial-body" style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 22, lineHeight: 1.6 }}>
            Mux is finalizing your video. You can edit the title or room here. Members can watch it back as a replay.
          </p>

          <label className="overline">Title</label>
          <input
            className="input-belle"
            value={finishOpts.title}
            onChange={(e) => setFinishOpts({ ...finishOpts, title: e.target.value })}
            data-testid="finish-title"
          />

          <div style={{ marginTop: 18 }}>
            <label className="overline">Room</label>
            <select
              className="input-belle"
              value={finishOpts.section}
              onChange={(e) => setFinishOpts({ ...finishOpts, section: e.target.value })}
              style={{ width: "100%" }}
            >
              {SECTIONS.map((s) => (
                <option key={s.slug} value={s.slug}>{s.label}</option>
              ))}
            </select>
          </div>

          <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 12 }}>
            <ToggleRow
              label="Save to /live archive"
              checked={finishOpts.save_to_archive}
              onChange={(v) => setFinishOpts({ ...finishOpts, save_to_archive: v })}
            />
            <ToggleRow
              label="Show on my Broadcasts tab"
              checked={finishOpts.show_on_profile}
              onChange={(v) => setFinishOpts({ ...finishOpts, show_on_profile: v })}
            />
            <ToggleRow
              label="Also post to the room as a sharable post"
              checked={finishOpts.post_to_room}
              onChange={(v) => setFinishOpts({ ...finishOpts, post_to_room: v })}
            />
          </div>

          <div style={{ display: "flex", gap: 12, marginTop: 28, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => confirmFinish(false)}
              disabled={finishing}
              data-testid="finish-discard"
              style={{ color: "var(--brand-accent)" }}
            >
              Discard
            </button>
            <button
              type="button"
              className="btn-primary"
              data-testid="finish-save"
              onClick={() => confirmFinish(true)}
              disabled={finishing}
              style={{ background: "var(--brand-accent)" }}
            >
              {finishing ? "Saving…" : "Save"}
            </button>
          </div>
        </ModalShell>
      )}

      {/* SCHEDULE MODAL */}
      {showScheduleModal && (
        <ModalShell onClose={() => setShowScheduleModal(false)}>
          <div className="overline" style={{ color: "var(--brand-accent)" }}>Schedule a broadcast</div>
          <h2 className="editorial-h2" style={{ fontSize: 28, marginTop: 8, marginBottom: 20 }}>
            When shall they gather?
          </h2>
          <label className="overline" style={{ display: "block", marginBottom: 6 }}>Title</label>
          <input
            data-testid="schedule-title"
            type="text"
            value={schedDraft.title}
            onChange={(e) => setSchedDraft({ ...schedDraft, title: e.target.value })}
            placeholder="e.g. Sunday letter, read aloud"
            className="input-belle"
            style={{ width: "100%", marginBottom: 18 }}
          />
          <label className="overline" style={{ display: "block", marginBottom: 6 }}>Date &amp; time</label>
          <input
            data-testid="schedule-datetime"
            type="datetime-local"
            value={schedDraft.scheduled_at}
            onChange={(e) => setSchedDraft({ ...schedDraft, scheduled_at: e.target.value })}
            className="input-belle"
            style={{ width: "100%", marginBottom: 18 }}
          />
          <label className="overline" style={{ display: "block", marginBottom: 6 }}>Room (optional)</label>
          <select
            data-testid="schedule-section"
            value={schedDraft.section}
            onChange={(e) => setSchedDraft({ ...schedDraft, section: e.target.value })}
            className="input-belle"
            style={{ width: "100%", marginBottom: 18 }}
          >
            {SECTIONS.map((s) => (
              <option key={s.slug} value={s.slug}>{s.label}</option>
            ))}
          </select>
          <label className="overline" style={{ display: "block", marginBottom: 6 }}>A few words (optional)</label>
          <textarea
            data-testid="schedule-description"
            value={schedDraft.description}
            onChange={(e) => setSchedDraft({ ...schedDraft, description: e.target.value })}
            rows={3}
            placeholder="What members can expect — tone, length, theme."
            className="input-belle"
            style={{ width: "100%", marginBottom: 18, fontFamily: "'Cormorant Garamond', serif", fontSize: 15 }}
          />
          <ToggleRow
            label="Send a one-time 'Save the date' push to everyone now"
            checked={schedDraft.send_save_the_date_push}
            onChange={(v) => setSchedDraft({ ...schedDraft, send_save_the_date_push: v })}
          />
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 24 }}>
            <button type="button" className="btn-secondary" onClick={() => setShowScheduleModal(false)}>Cancel</button>
            <button
              type="button"
              data-testid="confirm-schedule"
              className="btn-primary"
              disabled={scheduling}
              onClick={submitSchedule}
              style={{ background: "var(--brand-accent)" }}
            >
              {scheduling ? "Scheduling…" : "Schedule"}
            </button>
          </div>
        </ModalShell>
      )}

      <style>{`@keyframes lvpulse { 0%,100%{opacity:1} 50%{opacity:.35} } @keyframes lvspin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        @media (max-width: 640px) {
          [data-testid="open-go-live-modal"], [data-testid="open-upload-modal"], [data-testid="open-schedule-modal"] {
            width: 100%;
            justify-content: center !important;
            padding: 14px 20px !important;
            font-size: 13px !important;
            min-height: 48px;
          }
        }
      `}</style>

      {/* UPLOAD VIDEO MODAL */}
      {showUploadModal && (
        <ModalShell onClose={closeUploadModal}>
          {uploadStage === "form" && (
            <>
              <div className="overline" style={{ color: "var(--brand-accent)" }}>Upload a broadcast</div>
              <h2 className="editorial-h2" style={{ fontSize: 28, marginTop: 8, marginBottom: 20 }}>
                A pre-recorded edition.
              </h2>
              <label className="overline" style={{ display: "block", marginBottom: 6 }}>Title</label>
              <input
                data-testid="upload-title"
                type="text"
                value={uploadDraft.title}
                onChange={(e) => setUploadDraft({ ...uploadDraft, title: e.target.value })}
                placeholder="A quiet Sunday morning"
                className="input-belle"
                style={{ width: "100%", marginBottom: 18 }}
                autoFocus
              />
              <label className="overline" style={{ display: "block", marginBottom: 6 }}>Room</label>
              <select
                data-testid="upload-section"
                value={uploadDraft.section}
                onChange={(e) => setUploadDraft({ ...uploadDraft, section: e.target.value })}
                className="input-belle"
                style={{ width: "100%", marginBottom: 18 }}
              >
                {SECTIONS.map((s) => (
                  <option key={s.slug} value={s.slug}>{s.label}</option>
                ))}
              </select>
              <label className="overline" style={{ display: "block", marginBottom: 6 }}>A few words (optional)</label>
              <textarea
                data-testid="upload-description"
                value={uploadDraft.description}
                onChange={(e) => setUploadDraft({ ...uploadDraft, description: e.target.value })}
                rows={3}
                placeholder="What members will hear or see."
                className="input-belle"
                style={{ width: "100%", marginBottom: 18, fontFamily: "'Cormorant Garamond', serif", fontSize: 15 }}
              />
              <p className="editorial-body" style={{ fontSize: 13, color: "var(--text-muted)", fontStyle: "italic", marginBottom: 8 }}>
                No length limit. Once uploaded, Mux will encode it and it&apos;ll appear in your archive.
              </p>
              <div style={{ marginTop: 14, marginBottom: 6 }}>
                <ToggleRow
                  label="Send a push notification to subscribers when it's ready"
                  checked={uploadDraft.notify_subscribers}
                  onChange={(v) => setUploadDraft({ ...uploadDraft, notify_subscribers: v })}
                />
              </div>
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 24 }}>
                <button type="button" className="btn-secondary" onClick={closeUploadModal}>Cancel</button>
                <button
                  type="button"
                  data-testid="upload-choose-file"
                  className="btn-primary"
                  disabled={preparingUpload}
                  onClick={beginUpload}
                  style={{ background: "var(--brand-accent)" }}
                >
                  {preparingUpload ? "Preparing…" : "Choose file"}
                </button>
              </div>
            </>
          )}

          {uploadStage === "uploading" && uploadEndpoint && (
            <>
              <div className="overline" style={{ color: "var(--brand-accent)" }}>Uploading</div>
              <h2 className="editorial-h2" style={{ fontSize: 26, marginTop: 8, marginBottom: 18 }}>
                {uploadDraft.title}
              </h2>
              <div
                style={{
                  border: "1px dashed var(--border-default)",
                  padding: 20,
                  borderRadius: 4,
                  background: "var(--bg-alt)",
                }}
                data-testid="upload-uploader-wrapper"
              >
                <MuxUploader
                  endpoint={uploadEndpoint.upload_url}
                  chunkSize={5120}
                  onSuccess={handleUploadSuccess}
                  onUploadError={(e) => {
                    const msg = e?.detail?.message || e?.message || "Upload failed. Please check your connection and try again.";
                    toast.error(msg);
                  }}
                  style={{
                    "--uploader-font-family": "'Cormorant Garamond', serif",
                    "--uploader-primary-color": "var(--brand-accent)",
                  }}
                />
              </div>
              <p className="editorial-body" style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", marginTop: 16, textAlign: "center" }}>
                Keep this window open until the upload completes. On mobile, please stay on Wi-Fi if possible.
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                <button type="button" className="btn-secondary" onClick={closeUploadModal} data-testid="upload-cancel">
                  Cancel
                </button>
              </div>
            </>
          )}

          {uploadStage === "complete" && (
            <>
              <div className="overline" style={{ color: "var(--brand-accent)" }}>Received</div>
              <h2 className="editorial-h2" style={{ fontSize: 26, marginTop: 8, marginBottom: 14 }}>
                Encoding now.
              </h2>
              <p className="editorial-body" style={{ fontSize: 15, color: "var(--text-secondary)", lineHeight: 1.6 }}>
                Your video is uploaded. Mux is preparing the replay — it&apos;ll appear in the archive within about a minute.
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={closeUploadModal}
                  data-testid="upload-done"
                  style={{ background: "var(--brand-accent)" }}
                >
                  Done
                </button>
              </div>
            </>
          )}
        </ModalShell>
      )}
    </div>
  );
};

const ModalShell = ({ children, onClose }) => (
  <div
    onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(51,51,51,0.45)",
      backdropFilter: "blur(10px)",
      zIndex: 500,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
    }}
  >
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border-default)",
        maxWidth: 520,
        width: "100%",
        padding: 32,
        boxShadow: "0 24px 64px rgba(51,51,51,0.18)",
        maxHeight: "calc(100vh - 32px)",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {children}
    </div>
  </div>
);

const ToggleRow = ({ label, checked, onChange }) => (
  <label
    style={{
      display: "flex",
      alignItems: "center",
      gap: 14,
      cursor: "pointer",
      padding: "8px 0",
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      style={{ accentColor: "var(--brand-accent)", width: 18, height: 18 }}
    />
    <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17 }}>{label}</span>
  </label>
);

export default Live;
